import { centroid } from '@turf/turf';
import dayjs from 'dayjs';
import type { Feature, Polygon } from 'geojson';

import type { FloodJob } from '@/mocks/floodJobs';
import type { AoiRemoteTimeWindows, EnhancedFloodPoint } from '@/types/flood.types';

import { buildEnhancedFloodPointFromDemoRegion } from './enhancedFloodDataGenerator';
import { inferDistrictLabel, inferProvinceFromRegionName } from './floodDemoRegionGeojson';

const HANOI_FALLBACK_LNG_LAT: [number, number] = [105.8342, 21.0278];

function jobCentroid(job: FloodJob): [number, number] {
  const poly = job.aoiPolygon;
  if (!poly?.coordinates?.[0]?.length) return HANOI_FALLBACK_LNG_LAT;
  const feat: Feature<Polygon> = { type: 'Feature', properties: {}, geometry: poly };
  const c = centroid(feat);
  const [lng, lat] = c.geometry.coordinates;
  return [lng, lat];
}

function crisisReferenceIso(job: FloodJob): string | null {
  const p = job.parameters;
  const raw = p.crisisEnd ?? p.crisis;
  if (raw == null || String(raw).trim() === '') return null;
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return raw;
  return d.toISOString();
}

function crisisYear(job: FloodJob): number {
  const iso = crisisReferenceIso(job);
  if (!iso) return new Date().getFullYear();
  const y = new Date(iso).getFullYear();
  return Number.isNaN(y) ? new Date().getFullYear() : y;
}

/**
 * Cùng logic mặc định `baselineEnd` / `crisisEnd` như {@link buildMockSarSceneGalleryFromParameters}.
 */
function remoteTimeWindowsFromJobParams(
  parameters: FloodJob['parameters'],
): AoiRemoteTimeWindows | undefined {
  const baselineStart = dayjs(parameters.baseline);
  const baselineEnd = parameters.baselineEnd ? dayjs(parameters.baselineEnd) : baselineStart;
  const crisisStart = dayjs(parameters.crisis);
  const crisisEnd = parameters.crisisEnd ? dayjs(parameters.crisisEnd) : crisisStart;

  const b0 = baselineStart.isValid() ? baselineStart : dayjs();
  const b1 = baselineEnd.isValid() ? baselineEnd : b0;
  const c0 = crisisStart.isValid() ? crisisStart : dayjs();
  const c1 = crisisEnd.isValid() ? crisisEnd : c0;

  return {
    baseline: {
      startIso: b0.startOf('day').toISOString(),
      endIso: b1.endOf('day').toISOString(),
    },
    crisis: {
      startIso: c0.startOf('day').toISOString(),
      endIso: c1.endOf('day').toISOString(),
    },
  };
}

/**
 * Builds {@link EnhancedFloodPoint} (with `aoiContext`) for a completed / mock SAR job,
 * for reuse in the same AOI detail panel as the map popup.
 */
export function enhancedFloodPointFromFloodJob(job: FloodJob): EnhancedFloodPoint {
  const isLandCover = job.parameters.analysisKind === 'landCover';
  const position = jobCentroid(job);
  const floodedKm2 = job.results?.floodedAreaKm2;
  const aoiKm2 = job.aoiAreaKm2;
  const areaKm2 = Math.max(0.01, aoiKm2 ?? floodedKm2 ?? 2.5);

  // Chỉ dùng dữ liệu từ BE, không dùng logic mock/fallback tự sinh.
  const depthCm = job.results?.depthAtPointCm ?? 0;
  const population = job.results?.affectedPop ?? 0;
  const confidence = job.results?.accuracyScore != null ? Math.round(job.results.accuracyScore) : 0;

  const geoLabel = job.aoi.trim() || job.name;
  const districtLabel = inferDistrictLabel(geoLabel);
  const provinceLabel = inferProvinceFromRegionName(geoLabel);

  const refIso = crisisReferenceIso(job);
  const year = crisisYear(job);

  let point = buildEnhancedFloodPointFromDemoRegion({
    id: isLandCover ? `sar-land-cover-job-${job.id}` : `sar-job-${job.id}`,
    name: job.name,
    position,
    depthCm,
    areaKm2,
    population,
    provinceLabel,
    districtLabel,
    scenarioYear: year,
    referenceDateIso: refIso,
    ...(job.aoiPolygon ? { exportPolygon: job.aoiPolygon } : {}),
  });

  // Ghi đè các trường dữ liệu thực từ BE. Nếu BE không trả về thì để giá trị 0 hoặc null, không dùng số ngẫu nhiên.
  point = {
    ...point,
    confidence,
    nearbyInfrastructure: job.results?.nearbyInfrastructure ?? {
      hospitals: 0,
      schools: 0,
      emergencyShelters: 0,
      fireStations: 0,
      policeStations: 0,
      evacuationCenters: 0,
    },
    blockedRoads: job.results?.blockedRoads ?? [],
    damageAssessment: job.results?.damageAssessment ?? {
      affectedHouses: 0,
      damagedHouses: 0,
      destroyedHouses: 0,
      economicLossUSD: 0,
      agriculturalAreaHa: 0,
    },
  };

  if (point.aoiContext) {
    const res = job.results;
    point = {
      ...point,
      aoiContext: {
        ...point.aoiContext,
        estimatedDepthMinM: res?.estimatedDepthMinM ?? 0,
        estimatedDepthMaxM: res?.estimatedDepthMaxM ?? 0,
        affectedAreaPercent: res?.affectedAreaPercent ?? 0,
        ...(res?.landCoverImpacts ? { landCoverImpacts: res.landCoverImpacts as any } : {}),
      },
    };
  }

  // Chỉ gắn số liệu "diện tích ngập" cho job SAR flood — tab land cover chỉ render trong panel riêng.
  if (!isLandCover && floodedKm2 != null && point.aoiContext) {
    const ha = Math.round(floodedKm2 * 100 * 100) / 100;
    point = {
      ...point,
      aoiContext: {
        ...point.aoiContext,
        floodedAreaHa: job.results?.floodedAreaHa ?? ha,
      },
    };
  }

  const gallery = job.results?.sceneGallery;
  if (gallery?.length && point.aoiContext) {
    point = {
      ...point,
      aoiContext: {
        ...point.aoiContext,
        remoteSceneSlides: gallery.map((s) => ({
          role: s.role,
          periodLabel: s.periodLabel,
          referenceDate: s.referenceDate,
          imageUrl: s.imageUrl,
          caption: s.caption,
          geeFrames: s.geeFrames?.map((f) => ({
            index: f.index,
            rgbProfile: f.rgbProfile,
            imageUrl: f.imageUrl,
            caption: f.caption,
          })),
        })),
        remoteSceneGalleryLoading: job.results?.sceneGalleryLoading === true,
        ...(!isLandCover && job.results?.floodExtentGeometry != null
          ? { sarFloodExtentGeometry: job.results.floodExtentGeometry }
          : {}),
      },
    };
  } else if (!isLandCover && job.results?.floodExtentGeometry != null && point.aoiContext) {
    point = {
      ...point,
      aoiContext: {
        ...point.aoiContext,
        sarFloodExtentGeometry: job.results.floodExtentGeometry,
      },
    };
  }

  const remoteTimeWindows = remoteTimeWindowsFromJobParams(job.parameters);
  if (remoteTimeWindows && point.aoiContext) {
    point = {
      ...point,
      aoiContext: {
        ...point.aoiContext,
        remoteTimeWindows,
        vegetationCoverPct: job.results?.vegetationCoverPct,
      },
    };
  }

  return point;
}
